// services/claudeServiceV2.ts - WERSJA PRODUKCYJNA v3
// UWAGA: API Anthropic/OpenAI nie może być wywoływane bezpośrednio z przeglądarki (CORS)
// Zawsze używamy backend jako proxy!

import { plexiKnowledgeBase } from '../data/knowledgeBase';
import { chatbotCalculator } from '@/services/chatbotCalculatorService';
import { chatbotCalendar } from '@/services/chatbotCalendarService';

interface ClaudeResponse {
  content: string;
  metadata?: any;
  suggestedAction?: {
    type: string;
    data: any;
  };
}

// API URL
const API_URL = import.meta.env.PROD 
  ? 'https://api.plexisystem.pl/api'
  : 'http://localhost:3001/api';

export const claudeServiceV2 = {
  async sendMessage(message: string, context: any): Promise<ClaudeResponse> {
    try {
      console.log('=== CLAUDE SERVICE V2 (PRODUCTION) ===');
      console.log('Using backend Universal AI (CORS-safe)');
      
      // ZAWSZE używaj backend - bezpośrednie wywołania API są blokowane przez CORS!
      const BACKEND_URL = `${API_URL}/universal-ai/chat`;
      
      // Przygotuj system prompt z pełną bazą wiedzy
      const systemPrompt = `Jesteś asystentem AI PlexiSystem - ekspertem od produktów z pleksi i plexiglas.

TWOJA ROLA:
- Pomagasz klientom w doborze produktów z pleksi
- Doradzasz w kwestiach technicznych  
- Przygotowujesz wyceny na podstawie wymiarów
- Zbierasz dane kontaktowe potencjalnych klientów

BAZA WIEDZY O PRODUKTACH:
${JSON.stringify(plexiKnowledgeBase, null, 2)}

CENNIK:
- Plexi transparentna 3mm: 250 zł/m²
- Plexi transparentna 5mm: 350 zł/m²
- Plexi transparentna 8mm: 450 zł/m²
- Plexi transparentna 10mm: 550 zł/m²
- Plexi mleczna: +20% do ceny
- Plexi kolorowa: +30% do ceny
- Cięcie na wymiar: wliczone w cenę
- Gięcie: +50 zł/szt
- Druk UV: 150 zł/m²

INSTRUKCJE:
1. Zawsze bądź pomocny i profesjonalny
2. Używaj polskiego języka
3. Dodawaj emotikony dla przyjaznej komunikacji 😊
4. Gdy klient pyta o wycenę:
   - Zapytaj o wymiary (szerokość x wysokość w mm)
   - Zapytaj o grubość materiału
   - Zapytaj o ilość sztuk
   - Oblicz cenę na podstawie cennika
5. Gdy klient jest zainteresowany:
   - Delikatnie poproś o dane kontaktowe
   - Zaproponuj kontakt z handlowcem (tel: 884 042 107)
6. Zawsze podawaj konkretne informacje z bazy wiedzy

DANE KONTAKTOWE:
- Telefon: 884 042 107
- Email: oferty@plexisystem.pl
- Strona: www.plexisystem.pl`;

      // Przygotuj wiadomości dla backend
      const messages = [
        {
          role: 'system',
          content: systemPrompt
        }
      ];
      
      // Dodaj historię konwersacji (max 10 ostatnich)
      if (context?.previousMessages) {
        const recentMessages = context.previousMessages.slice(-10);
        recentMessages.forEach((msg: any) => {
          if (msg.role === 'user' || msg.role === 'assistant') {
            messages.push({
              role: msg.role,
              content: msg.content
            });
          }
        });
      }
      
      // Dodaj aktualną wiadomość
      messages.push({
        role: 'user',
        content: message
      });
      
      console.log(`Sending to backend with ${messages.length} messages`);
      
      // Wywołaj backend Universal AI
      const backendResponse = await fetch(BACKEND_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          module_name: 'chatbot',
          messages: messages,
          options: {
            max_tokens: 1000,
            temperature: 0.7
          }
        })
      });

      if (!backendResponse.ok) {
        const errorText = await backendResponse.text();
        console.error('Backend error:', errorText);
        
        // Jeśli backend nie działa, użyj inteligentnego fallbacka
        if (backendResponse.status === 404 || backendResponse.status === 500) {
          return this.getIntelligentFallback(message);
        }
        
        throw new Error(`Backend error: ${backendResponse.status}`);
      }

      const data = await backendResponse.json();
      
      // Normalizacja odpowiedzi z backendu
      let content = '';
      if (data.content && Array.isArray(data.content)) {
        const firstContent = data.content[0];
        if (firstContent && typeof firstContent === 'object' && firstContent.text) {
          content = firstContent.text;
        } else if (typeof firstContent === 'string') {
          content = firstContent;
        } else {
          content = JSON.stringify(data.content);
        }
      } else if (typeof data.content === 'string') {
        content = data.content;
      } else if (data.response) {
        content = data.response;
      } else if (data.text) {
        content = data.text;
      } else {
        console.warn('Unexpected response format:', data);
        content = 'Przepraszam, wystąpił problem z formatem odpowiedzi.';
      }
      
      // Sprawdź czy odpowiedź zawiera dane do kalkulacji
      const calculationData = await this.extractCalculationData(content, message);
      if (calculationData) {
        try {
          const result = await chatbotCalculator.calculate(calculationData);
          const calculationMessage = chatbotCalculator.formatResult(result, calculationData);
          content = content + '\n\n' + calculationMessage;
        } catch (error) {
          console.error('Błąd kalkulacji:', error);
        }
      }
      
      // Sprawdź czy odpowiedź dotyczy spotkania
      const meetingData = await chatbotCalendar.parseEventFromMessage(message, { leadInfo: context?.leadInfo });
      let suggestedAction = undefined;
      
      if (meetingData) {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const nextWeek = new Date();
        nextWeek.setDate(nextWeek.getDate() + 7);
        
        const availableSlots = await chatbotCalendar.findAvailableSlots(
          tomorrow,
          nextWeek,
          60
        );
        
        const meetingSuggestion = chatbotCalendar.formatMeetingSuggestion(availableSlots);
        content = content + '\n\n' + meetingSuggestion;
        
        suggestedAction = {
          type: 'schedule_meeting',
          data: meetingData
        };
      }
      
      return {
        content,
        metadata: { 
          model: data.model || 'claude-3-5-sonnet',
          provider: data.provider || 'anthropic',
          timestamp: new Date().toISOString(),
          hasCalculation: !!calculationData,
          hasMeeting: !!meetingData,
          source: 'backend_universal_ai',
          usage: data.usage
        },
        suggestedAction
      };
      
    } catch (error: any) {
      console.error('Error in claudeServiceV2:', error);
      
      // Inteligentny fallback gdy backend nie działa
      return this.getIntelligentFallback(message);
    }
  },
  
  getIntelligentFallback(message: string): ClaudeResponse {
    const lowerMessage = message.toLowerCase();
    
    // Analiza intencji użytkownika
    const isGreeting = /^(cześć|czesc|hej|witaj|dzień dobry|dobry wieczór|siema|hello|hi)/.test(lowerMessage);
    const isPricing = lowerMessage.includes('cen') || lowerMessage.includes('koszt') || lowerMessage.includes('wycen') || lowerMessage.includes('ile');
    const isProduct = lowerMessage.includes('ekspozytor') || lowerMessage.includes('stand') || lowerMessage.includes('gablo') || lowerMessage.includes('osłon');
    const isMaterial = lowerMessage.includes('plexi') || lowerMessage.includes('pleksi') || lowerMessage.includes('pmma') || lowerMessage.includes('materiał');
    const isContact = lowerMessage.includes('kontakt') || lowerMessage.includes('telefon') || lowerMessage.includes('mail') || lowerMessage.includes('adres');
    const isTechnical = lowerMessage.includes('grub') || lowerMessage.includes('wymiar') || lowerMessage.includes('gię') || lowerMessage.includes('cię');
    
    // Sprawdź czy ma konkretne dane do wyceny
    const hasSize = /\d+\s*[xX×]\s*\d+/.test(lowerMessage);
    const hasThickness = /\d+\s*mm/.test(lowerMessage);
    const hasQuantity = /\d+\s*(szt|sztuk)/.test(lowerMessage);
    
    // Powitanie
    if (isGreeting) {
      return {
        content: `Witaj! 👋 Jestem asystentem PlexiSystem!

Mogę pomóc Ci w:
• 📐 **Wycenie produktów z plexi** na wymiar
• 📦 **Doborze ekspozytorów** do Twojego biznesu  
• 🎨 **Wyborze odpowiedniego materiału**
• 💡 **Poradach technicznych**

Co Cię dzisiaj interesuje? 😊`,
        metadata: { fallback: true, intent: 'greeting' }
      };
    }
    
    // Wycena z konkretnymi danymi
    if (isPricing && hasSize && hasThickness) {
      const dimensionMatch = lowerMessage.match(/(\d+)\s*[xX×]\s*(\d+)/);
      const thicknessMatch = lowerMessage.match(/(\d+)\s*mm/);
      const quantityMatch = lowerMessage.match(/(\d+)\s*(szt|sztuk)/);
      
      if (dimensionMatch && thicknessMatch) {
        const width = parseInt(dimensionMatch[1]);
        const height = parseInt(dimensionMatch[2]);
        const thickness = parseInt(thicknessMatch[1]);
        const quantity = quantityMatch ? parseInt(quantityMatch[1]) : 1;
        
        // Określ materiał
        let material = 'transparentna';
        if (lowerMessage.includes('mlecz')) material = 'mleczna';
        if (lowerMessage.includes('kolor') || lowerMessage.includes('czarn') || lowerMessage.includes('czerwon')) material = 'kolorowa';
        
        // Cennik
        const prices: { [key: string]: number } = {
          '3': 250,
          '5': 350,
          '8': 450,
          '10': 550
        };
        
        const basePrice = prices[thickness.toString()] || 400;
        const materialMultiplier = material === 'mleczna' ? 1.2 : material === 'kolorowa' ? 1.3 : 1;
        
        const area = (width * height) / 1000000; // m²
        const unitPrice = area * basePrice * materialMultiplier;
        const totalPrice = unitPrice * quantity;
        
        return {
          content: `📊 **Wycena dla Ciebie:**

**Produkt:** Plexi ${material} ${thickness}mm
**Wymiary:** ${width} x ${height} mm
**Powierzchnia:** ${area.toFixed(2)} m²
**Ilość:** ${quantity} szt.

💰 **Cena jednostkowa:** ${unitPrice.toFixed(2)} zł netto
💰 **Cena całkowita:** ${totalPrice.toFixed(2)} zł netto

✅ Cena zawiera cięcie na wymiar
🚚 Transport wyceniamy indywidualnie

Zainteresowany? Skontaktuj się z nami:
📞 **884 042 107**
✉️ oferty@plexisystem.pl`,
          metadata: { fallback: true, calculated: true }
        };
      }
    }
    
    // Zapytanie o wycenę bez danych
    if (isPricing && (!hasSize || !hasThickness)) {
      return {
        content: `📊 **Chętnie przygotuję wycenę!**

Potrzebuję kilku informacji:

1️⃣ **Wymiary** - szerokość x wysokość (w mm)
2️⃣ **Grubość** - np. 3mm, 5mm, 8mm, 10mm
3️⃣ **Materiał** - transparentny, mleczny czy kolorowy?
4️⃣ **Ilość** - ile sztuk potrzebujesz?

**Przykład:** "Potrzebuję 5 sztuk plexi transparentnej 5mm o wymiarach 600x400mm"

💡 Mogę też zaproponować gotowe rozwiązania!`,
        metadata: { fallback: true, intent: 'pricing_request' }
      };
    }
    
    // Produkty
    if (isProduct) {
      return {
        content: `🎯 **Nasze ekspozytory i produkty z plexi:**

**📦 EKSPOZYTORY NA ULOTKI**
• Format A4, A5, DL, 1/3 A4
• Jedno- i wielokieszeniowe
• Stojące i wiszące
• Od 45 zł netto/szt.

**💄 STANDY KOSMETYCZNE**
• Na lakiery, perfumy, kremy
• Podświetlane LED
• Z grawerem logo
• Od 150 zł netto/szt.

**🍷 EKSPOZYTORY PREMIUM**
• Na alkohole i wina
• Gabloty zabezpieczające
• Displays obrotowe
• Od 250 zł netto/szt.

**⚡ NA ZAMÓWIENIE**
• Dowolny projekt
• Ekspresowa realizacja (3-5 dni)
• Grawer, druk UV, podświetlenie

Który rodzaj Cię interesuje? 😊`,
        metadata: { fallback: true, intent: 'products' }
      };
    }
    
    // Materiały
    if (isMaterial) {
      return {
        content: `🎨 **Materiały w naszej ofercie:**

**PLEXI (PMMA)**
• Transparentne - kryształowo czyste
• Mleczne (opal) - delikatnie rozpraszające światło
• Kolorowe - szeroka paleta barw
• Grubości: 2, 3, 4, 5, 6, 8, 10, 12, 15, 20mm
✅ Odporne na UV, łatwe w obróbce

**POLIWĘGLAN**
• 10x mocniejszy od plexi
• Idealny na osłony ochronne
• Grubości: 2-10mm
✅ Niezniszczalny, bezpieczny

**PCV/FOREX**
• Lekkie płyty piankowe
• Do reklam i oznakowania
• Grubości: 1-19mm
✅ Ekonomiczne rozwiązanie

**DIBOND**
• Płyty kompozytowe ALU
• Do kasetonów i reklam
• Grubość: 3mm
✅ Sztywne i eleganckie

Który materiał będzie najlepszy dla Twojego projektu? 🤔`,
        metadata: { fallback: true, intent: 'materials' }
      };
    }
    
    // Kontakt
    if (isContact) {
      return {
        content: `📞 **Dane kontaktowe PlexiSystem:**

**Zadzwoń do nas:**
☎️ 884 042 107
📱 Czynne: pon-pt 8:00-16:00

**Napisz do nas:**
✉️ oferty@plexisystem.pl
💬 Odpowiadamy w 24h

**Odwiedź nas:**
🌐 www.plexisystem.pl
📍 Warszawa

**Social Media:**
💼 LinkedIn: PlexiSystem
📘 Facebook: @plexisystem

Preferujesz kontakt telefoniczny czy mailowy? 😊`,
        metadata: { fallback: true, intent: 'contact' }
      };
    }
    
    // Porady techniczne
    if (isTechnical) {
      return {
        content: `🔧 **Porady techniczne:**

**GRUBOŚCI PLEXI:**
• 2-3mm - wizytówki, małe elementy
• 4-5mm - ekspozytory, stojaki (standard)
• 6-8mm - konstrukcje wolnostojące
• 10mm+ - elementy nośne, gabloty

**OBRÓBKA:**
• Cięcie laserowe - precyzja 0.1mm
• Frezowanie CNC - skomplikowane kształty
• Gięcie na gorąco - promienie od 50mm
• Klejenie - niewidoczne połączenia

**WYKOŃCZENIE:**
• Polerowanie - kryształowe krawędzie
• Grawer laserowy - trwałe logo
• Druk UV - pełny kolor
• Podświetlenie LED - efekt WOW

Jakie szczegóły techniczne Cię interesują? 🛠️`,
        metadata: { fallback: true, intent: 'technical' }
      };
    }
    
    // Domyślna odpowiedź
    return {
      content: `👋 Dziękuję za wiadomość!

Jestem asystentem PlexiSystem i chętnie pomogę w:
• 📐 Wycenie produktów na wymiar
• 📦 Doborze ekspozytorów
• 🎨 Wyborze materiałów
• 💡 Poradach technicznych

**Zadaj mi konkretne pytanie, np:**
- "Ile kosztuje plexi 5mm 600x400?"
- "Potrzebuję ekspozytora na ulotki A4"
- "Jaki materiał na osłonę ochronną?"

Lub zadzwoń bezpośrednio:
📞 **884 042 107**

W czym mogę pomóc? 😊`,
      metadata: { fallback: true, intent: 'default' }
    };
  },
  
  async extractCalculationData(claudeResponse: string, userMessage: string): Promise<any | null> {
    const lowerMessage = userMessage.toLowerCase();
    
    // Wyrażenia regularne do wyciągania danych
    const dimensionRegex = /(\d+)\s*[xX×]\s*(\d+)\s*(?:mm)?/;
    const quantityRegex = /(\d+)\s*(?:szt|sztuk)/;
    const thicknessRegex = /(?:^|\s)(\d+)\s*mm(?:\s+grubo[śs]|\s+gruby|\s*$)/i;
    
    const dimensions = lowerMessage.match(dimensionRegex);
    const quantity = lowerMessage.match(quantityRegex);
    const thickness = lowerMessage.match(thicknessRegex);
    
    // Materiały
    const materials = [
      { name: 'Plexi transparentna', keywords: ['transparentn', 'przezroczyst', 'bezbarwn', 'clear'] },
      { name: 'Plexi mleczna', keywords: ['mleczn', 'opalow', 'matow', 'opal'] },
      { name: 'Plexi czarna', keywords: ['czarn', 'black'] },
      { name: 'Plexi kolorowa', keywords: ['kolorow', 'barwion', 'czerwon', 'niebiesk', 'zielon'] },
      { name: 'PCV', keywords: ['pcv', 'pvc', 'forex'] },
      { name: 'Dibond', keywords: ['dibond', 'kompozyt', 'alu'] }
    ];
    
    let material = null;
    for (const mat of materials) {
      if (mat.keywords.some(keyword => lowerMessage.includes(keyword))) {
        material = mat.name;
        break;
      }
    }
    
    // Jeśli nie ma materiału ale wspomina plexi, domyślnie transparentna
    if (!material && (lowerMessage.includes('plexi') || lowerMessage.includes('pleksi'))) {
      material = 'Plexi transparentna';
    }
    
    // Jeśli mamy wszystkie dane, zwróć obiekt do kalkulacji
    if (dimensions && material && thickness) {
      return {
        material,
        thickness: parseInt(thickness[1]),
        width: parseInt(dimensions[1]),
        height: parseInt(dimensions[2]),
        quantity: quantity ? parseInt(quantity[1]) : 1,
        processing: {
          cutting: true, // domyślnie włączone
          bending: lowerMessage.includes('gię') || lowerMessage.includes('gni') || lowerMessage.includes('zagię'),
          printing: lowerMessage.includes('druk') || lowerMessage.includes('nadru') || lowerMessage.includes('print'),
          engraving: lowerMessage.includes('grawer') || lowerMessage.includes('logo')
        }
      };
    }
    
    return null;
  }
};
